// frontend/src/pages/admin/AdminResetPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { resetApi } from '../../api/reset';
import { backupsApi } from '../../api/backups';
import { settingsApi } from '../../api/settings';
import { 
    FaExclamationTriangle, FaTrash, FaDownload, FaSpinner, 
    FaShieldAlt, FaCheckCircle, 
    FaUpload, FaHistory, FaCloud, FaClock, FaSave, FaCheckSquare, FaSquare, FaPlus, FaPlay, FaPause, FaBomb,
    FaCogs, FaRobot, FaShippingFast 
} from 'react-icons/fa';

const MASTER_ADMIN_EMAILS = ["esekiegabriel@gmail.com", "thomasesekie@gmail.com"];

function AdminResetPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // --- LOADING STATES ---
  const [loading, setLoading] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const [confirmWord, setConfirmWord] = useState('');
  const [results, setResults] = useState(null);
  const [restoreResults, setRestoreResults] = useState(null);
  
  // Cloud Backup State
  const [cloudBackups, setCloudBackups] = useState([]);
  const [selectedCloudFiles, setSelectedCloudFiles] = useState([]);
  const [backupConfig, setBackupConfig] = useState({ enabled: false, interval: 'daily' });

  // Reset Options
  const [options, setOptions] = useState({
    backup_enabled: true,
    ignore_audit_logs: false,
    ignore_ai_chat_logs: false,
    ignore_system_settings: true,
    ignore_shipping_rates: true,
    ignore_users: false,
    ignore_parts: false,
    ignore_marketplace: false,
    ignore_stores: false,
    keep_only_me: true,
    keep_admins: false,
    wipe_user_data_only: false 
  });

  // Derived state for "Total Wipe" checkbox
  const isTotalWipe = (
      !options.ignore_audit_logs &&
      !options.ignore_ai_chat_logs &&
      !options.ignore_system_settings &&
      !options.ignore_shipping_rates &&
      !options.ignore_users &&
      !options.ignore_parts &&
      !options.ignore_marketplace &&
      !options.ignore_stores &&
      options.keep_only_me &&
      !options.keep_admins &&
      !options.wipe_user_data_only
  );

  useEffect(() => {
    if (user && !MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase())) {
      alert("Access Denied: Master Admin Only.");
      navigate('/admin');
    } else {
        fetchCloudData();
    }
  }, [user, navigate]);

  const fetchCloudData = async () => {
      try {
          const [files, settings] = await Promise.all([
              backupsApi.listBackups(),
              settingsApi.getAllSettings()
          ]);
          setCloudBackups(files);
          setBackupConfig(settings.backup_config || { enabled: false, interval: 'daily' });
      } catch (e) { console.error(e); }
  };

  const handleToggle = (key) => {
    setOptions(prev => {
        const newOptions = { ...prev, [key]: !prev[key] };
        if (key === 'keep_only_me' && newOptions.keep_only_me) newOptions.keep_admins = false;
        if (key === 'keep_admins' && newOptions.keep_admins) newOptions.keep_only_me = false;
        return newOptions;
    });
  };

  const handleTotalWipeToggle = () => {
      if (isTotalWipe) {
          setOptions(prev => ({
              ...prev,
              ignore_audit_logs: true,
              ignore_ai_chat_logs: true,
              ignore_system_settings: true,
              ignore_shipping_rates: true,
              ignore_users: true,
              ignore_parts: true,
              ignore_marketplace: true,
              ignore_stores: true,
              keep_only_me: true
          }));
      } else {
          setOptions(prev => ({
              ...prev,
              ignore_audit_logs: false,
              ignore_ai_chat_logs: false,
              ignore_system_settings: false,
              ignore_shipping_rates: false,
              ignore_users: false,
              ignore_parts: false,
              ignore_marketplace: false,
              ignore_stores: false,
              keep_only_me: true,
              keep_admins: false,
              wipe_user_data_only: false
          }));
      }
  };

  const handleSaveConfig = async () => {
      setSavingConfig(true);
      try {
          await settingsApi.updateSettings('backup_config', backupConfig);
          alert("Backup schedule updated successfully!");
          fetchCloudData();
      } catch (e) { alert("Failed to update schedule."); }
      finally { setSavingConfig(false); }
  };

  const handleManualCloudBackup = async () => {
      setIsBackingUp(true);
      try {
          await backupsApi.triggerManualBackup();
          alert("Manual Cloud Backup Created!");
          fetchCloudData();
      } catch (e) { alert("Backup failed."); }
      finally { setIsBackingUp(false); }
  };

  const handleCloudRestore = async (filename) => {
      if (!window.confirm(`Restore system using ${filename}? Current data will be overwritten.`)) return;
      setLoading(true);
      setResults(null);
      setRestoreResults(null);
      try {
          const res = await backupsApi.restoreFromCloud(filename);
          setRestoreResults(res.details);
          alert("System Restored from Cloud!");
      } catch (e) { alert("Restore failed."); }
      finally { setLoading(false); }
  };

  const handleSelectAllCloud = () => {
      if (selectedCloudFiles.length === cloudBackups.length) {
          setSelectedCloudFiles([]);
      } else {
          setSelectedCloudFiles(cloudBackups.map(f => f.name));
      }
  };

  const handleBulkDeleteCloud = async () => {
      if (selectedCloudFiles.length === 0) return;
      if (!window.confirm(`Delete ${selectedCloudFiles.length} backup files permanently?`)) return;
      
      setIsDeleting(true);
      try {
          await backupsApi.deleteBulk(selectedCloudFiles);
          setSelectedCloudFiles([]);
          await fetchCloudData();
          alert("Files deleted successfully.");
      } catch (e) { 
          console.error(e);
          alert("Delete failed."); 
      } finally { 
          setIsDeleting(false); 
      }
  };

  const handleDownloadCloud = async (filename) => {
      setIsDownloading(filename);
      try {
          const data = await backupsApi.downloadBackup(filename);
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      } catch (e) {
          alert("Download failed.");
      } finally {
          setIsDownloading(null);
      }
  };

  const handleReset = async () => {
    if (confirmWord !== 'RESET') return;
    if (!window.confirm("FINAL WARNING: This will permanently delete data. Are you absolutely sure?")) return;

    setLoading(true);
    setResults(null);
    setRestoreResults(null);
    try {
      const response = await resetApi.executeReset(options);
      if (response.backup && options.backup_enabled) {
          const blob = new Blob([JSON.stringify(response.backup, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `EA_Local_Backup_${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      }
      setResults(response.details);
      alert("System Reset Completed Successfully.");
      fetchCloudData(); 
    } catch (err) {
      alert("Reset Failed: " + (err.data?.message || err.message));
    } finally {
      setLoading(false);
      setConfirmWord('');
    }
  };

  const handleRestoreFile = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!window.confirm("This will overwrite existing data with the backup content. Proceed?")) {
          e.target.value = null;
          return;
      }

      const reader = new FileReader();
      reader.onload = async (event) => {
          try {
              const jsonData = JSON.parse(event.target.result);
              setLoading(true);
              setResults(null);
              const response = await resetApi.restoreBackup(jsonData);
              setRestoreResults(response.details);
              alert("System Restored Successfully!");
          } catch (err) {
              alert("Restore Failed: " + (err.data?.message || err.message || "Invalid JSON file"));
          } finally {
              setLoading(false);
              e.target.value = null;
          }
      };
      reader.readAsText(file);
  };

  if (user && !MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase())) return null;

  return (
    <div className="max-w-7xl mx-auto pb-20 px-4 md:px-6">
      
      {/* --- HEADER --- */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-3 mb-8 border-b border-gray-200 pb-4">
          <div className="bg-red-600 text-white p-3 rounded-lg shadow-lg">
              <FaShieldAlt size={24} />
          </div>
          <div>
              <h1 className="text-2xl font-bold text-gray-800">System Maintenance & Cloud Backups</h1>
              <p className="text-sm text-red-600 font-bold flex items-center gap-1">
                  <FaShieldAlt /> MASTER ADMIN CONSOLE
              </p>
          </div>
      </div>

      {/* --- SECTION 1: CLOUD BACKUP REGISTRY --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
          
          {/* --- COLUMN 1: AUTOMATION SETTINGS --- */}
          <div className="lg:col-span-1 space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 border-b pb-2 gap-2">
                      <h3 className="font-bold text-gray-800 flex items-center gap-2">
                          <FaClock className="text-blue-500" /> Automation
                      </h3>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 ${backupConfig.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {backupConfig.enabled ? <><FaPlay size={8} /> ACTIVE</> : <><FaPause size={8} /> PAUSED</>}
                      </span>
                  </div>

                  <div className="space-y-4">
                      <label className="flex items-center gap-3 p-3 rounded bg-blue-50 cursor-pointer border border-blue-100">
                          <input 
                            type="checkbox" 
                            checked={backupConfig.enabled} 
                            onChange={e => setBackupConfig({...backupConfig, enabled: e.target.checked})}
                            className="h-5 w-5 text-primary rounded" 
                          />
                          <span className="font-bold text-sm text-blue-800">Enable Auto-Backups</span>
                      </label>

                      <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Backup Interval</label>
                          <select 
                            value={backupConfig.interval}
                            onChange={e => setBackupConfig({...backupConfig, interval: e.target.value})}
                            className="w-full p-2 border rounded bg-white text-sm"
                          >
                              <option value="minute">Every Minute (Testing)</option>
                              <option value="hour">Every Hour</option>
                              <option value="2h">Every 2 Hours</option>
                              <option value="6h">Every 6 Hours</option>
                              <option value="daily">Every Day (Midnight)</option>
                              <option value="2days">Every 2 Days</option>
                              <option value="weekly">Weekly (Sunday)</option>
                              <option value="monthly">Monthly (1st of Month)</option>
                          </select>
                      </div>

                      <button 
                        onClick={handleSaveConfig}
                        disabled={savingConfig}
                        className="w-full bg-black text-white py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:opacity-80 transition-opacity shadow-md"
                      >
                          {savingConfig ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Schedule
                      </button>
                  </div>
              </div>

              {/* --- DANGER ZONE CARD --- */}
              <div className="bg-red-50 p-6 rounded-xl border border-red-100">
                  <h3 className="font-bold text-red-800 mb-2 flex items-center gap-2"><FaExclamationTriangle /> Danger Zone</h3>
                  <p className="text-xs text-red-700 mb-4">Wipe dynamic data based on the configuration below. Structural data is always safe.</p>
                  <div className="flex flex-col gap-2">
                      <input 
                        type="text" value={confirmWord} onChange={(e) => setConfirmWord(e.target.value)}
                        placeholder="Type RESET"
                        className="w-full p-3 border-2 border-red-200 rounded-lg text-center font-black tracking-widest focus:border-red-500 outline-none"
                      />
                      <button 
                        onClick={handleReset} disabled={loading || confirmWord !== 'RESET'}
                        className={`w-full py-3 rounded-lg font-black text-white shadow-md transition-all flex items-center justify-center gap-2 ${confirmWord === 'RESET' ? 'bg-red-600 hover:bg-red-700 scale-105' : 'bg-gray-300 cursor-not-allowed'}`}
                      >
                        {loading ? <FaSpinner className="animate-spin" /> : <FaTrash />} WIPE SYSTEM
                      </button>
                  </div>
              </div>
          </div>

          {/* --- COLUMN 2 & 3: CLOUD REGISTRY --- */}
          <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[500px]">
                  <div className="p-4 bg-gray-50 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <h3 className="font-bold text-gray-800 flex items-center gap-2"><FaCloud className="text-primary" /> Cloud Backup Registry</h3>
                      <div className="flex gap-2 w-full sm:w-auto">
                          {selectedCloudFiles.length > 0 && (
                              <button 
                                onClick={handleBulkDeleteCloud} 
                                disabled={isDeleting}
                                className="flex-1 sm:flex-none bg-red-100 text-red-600 px-3 py-2 rounded text-xs font-bold hover:bg-red-200 flex items-center justify-center gap-1"
                              >
                                  {isDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />} Delete ({selectedCloudFiles.length})
                              </button>
                          )}
                          <button 
                            onClick={handleManualCloudBackup} 
                            disabled={isBackingUp} 
                            className="flex-1 sm:flex-none bg-primary text-white px-3 py-2 rounded text-xs font-bold hover:bg-primary-dark flex items-center justify-center gap-1"
                          >
                              {isBackingUp ? <FaSpinner className="animate-spin" /> : <FaPlus />} Backup Now
                          </button>
                      </div>
                  </div>

                  <div className="flex-grow overflow-y-auto overflow-x-auto">
                      <table className="w-full text-left text-sm min-w-[600px]">
                          <thead className="bg-gray-100 text-gray-500 uppercase text-[10px] sticky top-0">
                              <tr>
                                  <th className="p-3 w-10">
                                      <button onClick={handleSelectAllCloud}>
                                          {cloudBackups.length > 0 && selectedCloudFiles.length === cloudBackups.length ? <FaCheckSquare className="text-primary" /> : <FaSquare className="text-gray-300" />}
                                      </button>
                                  </th>
                                  <th className="p-3">Filename</th>
                                  <th className="p-3">Size</th>
                                  <th className="p-3">Date</th>
                                  <th className="p-3 text-right">Action</th>
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                              {cloudBackups.length === 0 ? (
                                  <tr><td colSpan="5" className="p-10 text-center text-gray-400 italic">No cloud backups found.</td></tr>
                              ) : (
                                  cloudBackups.map(file => (
                                      <tr key={file.name} className="hover:bg-gray-50">
                                          <td className="p-3">
                                              <button onClick={() => {
                                                  setSelectedCloudFiles(prev => prev.includes(file.name) ? prev.filter(f => f !== file.name) : [...prev, file.name])
                                              }}>
                                                  {selectedCloudFiles.includes(file.name) ? <FaCheckSquare className="text-primary" /> : <FaSquare className="text-gray-300" />}
                                              </button>
                                          </td>
                                          <td className="p-3 font-mono text-xs text-gray-700 truncate max-w-[200px]" title={file.name}>{file.name}</td>
                                          <td className="p-3 text-gray-500">{file.size_kb} KB</td>
                                          <td className="p-3 text-gray-500">{new Date(file.created_at).toLocaleString()}</td>
                                          <td className="p-3 text-right flex justify-end gap-2">
                                              <button 
                                                onClick={() => handleDownloadCloud(file.name)}
                                                disabled={isDownloading === file.name}
                                                className="text-green-600 hover:bg-green-50 p-2 rounded"
                                                title="Download"
                                              >
                                                  {isDownloading === file.name ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                                              </button>
                                              <button 
                                                onClick={() => handleCloudRestore(file.name)}
                                                className="text-blue-600 hover:underline font-bold flex items-center gap-1 text-xs p-2"
                                              >
                                                  <FaHistory /> Restore
                                              </button>
                                          </td>
                                      </tr>
                                  ))
                              )}
                          </tbody>
                      </table>
                  </div>
              </div>
          </div>
      </div>

      {/* --- SECTION 2: RESET CONFIGURATION --- */}
      <div className="bg-gray-100 p-6 rounded-xl border border-gray-300 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FaCogs /> Reset Configuration
              </h2>
              
              {/* --- TOTAL WIPE CHECKBOX --- */}
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all w-full sm:w-auto justify-center ${isTotalWipe ? 'bg-red-600 border-red-600 text-white shadow-md' : 'bg-white border-red-200 text-red-600 hover:bg-red-50'}`}>
                  <input 
                      type="checkbox" 
                      checked={isTotalWipe} 
                      onChange={handleTotalWipeToggle}
                      className="hidden" 
                  />
                  <FaBomb className={isTotalWipe ? "animate-pulse" : ""} />
                  <span className="font-bold text-sm">CLEAN DATABASE COMPLETELY</span>
              </label>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* Group 1: Backup & Logs */}
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="font-bold text-gray-700 mb-3 border-b pb-2 text-sm uppercase">Backup & Logs</h3>
                  <div className="space-y-2">
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.backup_enabled} onChange={() => handleToggle('backup_enabled')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm">Download Local JSON Backup</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.ignore_audit_logs} onChange={() => handleToggle('ignore_audit_logs')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm">Ignore Audit Logs</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.ignore_ai_chat_logs} onChange={() => handleToggle('ignore_ai_chat_logs')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm font-bold flex items-center gap-2"><FaRobot className="text-purple-500" /> Ignore AI Chat Logs</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.ignore_system_settings} onChange={() => handleToggle('ignore_system_settings')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm font-bold flex items-center gap-2"><FaCogs className="text-blue-500" /> Ignore System Settings</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.ignore_shipping_rates} onChange={() => handleToggle('ignore_shipping_rates')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm font-bold flex items-center gap-2"><FaShippingFast className="text-green-500" /> Ignore Shipping Rates</span>
                      </label>
                  </div>
              </div>

              {/* Group 2: User Logic */}
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="font-bold text-gray-700 mb-3 border-b pb-2 text-sm uppercase">User Accounts</h3>
                  <div className="space-y-2">
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.ignore_users} onChange={() => handleToggle('ignore_users')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm">Ignore Registered Users (Keep All)</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.keep_only_me} onChange={() => handleToggle('keep_only_me')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm">Keep Only Me (Master Admin)</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.keep_admins} onChange={() => handleToggle('keep_admins')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm">Keep Me and All Admins</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.wipe_user_data_only} onChange={() => handleToggle('wipe_user_data_only')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm">Wipe Data Only (Keep Accounts)</span>
                      </label>
                  </div>
              </div>

              {/* Group 3: Inventory & Entities */}
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="font-bold text-gray-700 mb-3 border-b pb-2 text-sm uppercase">Inventory & Entities</h3>
                  <div className="space-y-2">
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.ignore_parts} onChange={() => handleToggle('ignore_parts')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm">Ignore Parts & Reviews</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.ignore_marketplace} onChange={() => handleToggle('ignore_marketplace')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm">Ignore Vehicle Marketplace</span>
                      </label>
                      <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={options.ignore_stores} onChange={() => handleToggle('ignore_stores')} className="h-4 w-4 text-primary rounded" />
                          <span className="text-sm">Ignore Stores & Providers</span>
                      </label>
                  </div>
              </div>

          </div>
      </div>

      {/* --- SECTION 3: MANUAL RESTORE --- */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8 shadow-sm">
          <div className="flex flex-col sm:flex-row items-start gap-4">
              <FaHistory className="text-blue-500 text-3xl mt-1 flex-shrink-0 hidden sm:block" />
              <div className="flex-grow w-full">
                  <h2 className="text-lg font-bold text-blue-800 mb-2">Manual Restore (Local File)</h2>
                  <p className="text-sm text-blue-700 mb-4">
                      Upload a local <code>.json</code> backup file to restore the system if cloud backups are unavailable.
                  </p>
                  <label className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg cursor-pointer transition shadow-md gap-2 w-full sm:w-auto">
                      <FaUpload /> {loading ? 'Processing...' : 'Select Local Backup File'}
                      <input type="file" accept=".json" onChange={handleRestoreFile} className="hidden" disabled={loading} />
                  </label>
              </div>
          </div>
      </div>

      {/* RESULTS DISPLAY */}
      {(results || restoreResults) && (
          <div className="mt-10 bg-white p-6 rounded-xl shadow-lg border border-green-200 animate-fadeIn">
              <h3 className="font-bold text-green-700 mb-4 flex items-center gap-2">
                  <FaCheckCircle /> {results ? 'Reset Summary' : 'Restoration Summary'}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {Object.entries(results || restoreResults).map(([key, count]) => (
                      <div key={key} className="bg-gray-50 p-3 rounded border text-center">
                          <span className="block text-xl font-bold text-gray-800">{count}</span>
                          <span className="text-[10px] uppercase text-gray-500 font-bold">{key.replace('_', ' ')}</span>
                      </div>
                  ))}
              </div>
          </div>
      )}
    </div>
  );
}

export default AdminResetPage;